import classNames from "classnames";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { useMuiLocales } from "shared/locale/hooks";

import CancelIcon from "features/ui/Icons/CancelIcon";

const BrandingWrapper = ({ children }: { children: React.ReactNode }) => {
  const muiLocales = useMuiLocales();

  const theme = createTheme(
    {
      typography: {
        // IBM Plex Sans and Lato have no Japanese glyphs, so name a Japanese font per
        // OS (macOS, Windows, Linux) instead of relying on the generic fallback.
        fontFamily: `IBM Plex Sans, 'Lato', "Hiragino Sans", "Yu Gothic UI", "Noto Sans JP", sans-serif`,
      },
      components: {
        MuiTypography: {
          variants: [
            { props: { variant: "body3" }, style: {} },
            {
              props: { variant: "overline" },
              style: {},
            },
          ],
        },
        MuiButton: {
          defaultProps: {
            variant: "contained",
            color: "secondary",
            disableElevation: true,
          },
          variants: [
            {
              props: { color: "tertiary" },
              style: {},
            },
          ],
        },
        MuiIconButton: {
          defaultProps: {
            color: "transparent",
            size: "medium",
          },
          variants: [
            {
              props: { color: "tertiary" },
              style: {},
            },
            {
              props: { color: "transparent" },
              style: {},
            },
            {
              props: { size: "xs" },
              style: {},
            },
          ],
        },
        MuiTextField: {
          defaultProps: {
            size: "medium",
            variant: "outlined",
            type: "text",
          },
          variants: [
            {
              props: { size: "large" },
              style: {},
            },
          ],
        },
        MuiSelect: {
          defaultProps: {
            IconComponent: (props) => (
              <ArrowDropDownIcon
                {...props}
                className={classNames(props.className, "mr-2")}
              />
            ),
            size: "medium",
            variant: "outlined",
          },
          styleOverrides: {
            // Match VDS dropdown arrow (fg/default #393939, fg/disabled #C6C6C6).
            // Set here rather than via the icon's `sx` so it beats MUI's
            // SelectIcon styled wrapper, which `sx` loses to across the `as` boundary.
            icon: ({ theme }) => ({
              fontSize: "20px",
              color: theme.palette.text.primary,
              "&.Mui-disabled": { color: "var(--color-gray-200)" },
            }),
          },
          variants: [
            {
              props: { size: "large" },
              style: {},
            },
          ],
        },
        MuiRadio: {
          defaultProps: { size: "small", disableRipple: true },
        },
        MuiCheckbox: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiAlert: {
          defaultProps: {
            variant: "standard",
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true, // No more ripple, on the whole application 💣!
          },
        },
        MuiCard: {
          defaultProps: {
            elevation: 0,
            raised: false,
            variant: "outlined",
          },
        },
        MuiChip: {
          variants: [
            {
              props: { size: "large" },
              style: {},
            },
          ],
          defaultProps: {
            variant: "filled",
            deleteIcon: <CancelIcon />,
            size: "medium",
            color: "default",
          },
        },

        MuiSwitch: {
          defaultProps: {
            size: "medium",
          },
        },
        MuiAccordion: {
          defaultProps: {
            elevation: 0,
            disableGutters: true,
            square: false,
            slotProps: {
              transition: {
                timeout: 0,
                unmountOnExit: true,
              },
            },
          },
        },
        MuiBadge: {
          defaultProps: {
            color: "primary",
          },
        },
        MuiTooltip: {
          defaultProps: {
            arrow: true,
            disableTouchListener: true,
          },
        },
        MuiDialog: {
          defaultProps: {
            maxWidth: "md",
            fullWidth: true,
          },
        },
        MuiTabs: {
          defaultProps: {
            variant: "scrollable",
            scrollButtons: "auto",
          },
        },
        MuiTab: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            popupIcon: <ArrowDropDownIcon sx={{ fontSize: "20px" }} />,
            clearIcon: <CloseIcon sx={{ fontSize: 16 }} />,
            slotProps: {},
          },
          styleOverrides: {
            inputRoot: {
              padding: 0,
            },
          },
        },
        MuiPopover: {
          defaultProps: {
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          },
        },
        MuiStack: {
          defaultProps: {
            useFlexGap: true,
          },
        },
      },
      palette: {
        background: {
          default: "#F1F5F9", //slate-100
        },
        text: {
          primary: "#393939", //gray-700
          secondary: "#6F6F6F", //gray-500
        },
        primary: {
          main: "#525252", //gray-600
          contrastText: "#fff", //white
        },
        secondary: {
          main: "#6F6F6F",
          contrastText: "#fff",
        },
        success: {
          main: "#06DF73", //green-400
          light: "#7AF2A8", //green-300
          dark: "#00C950", //green-500
        },
        info: {
          main: "#50A2FF", //blue-400
          contrastText: "#FFFFFF", //white
          light: "#8EC6FF", //blue-300
          dark: "#2C7FFF", //blue-500
        },
        error: {
          main: "#FF6467", //red-400
          contrastText: "#FFFFFF", //white
          light: "#FFA2A3", //red-300
          dark: "#F4303B", //red-500
        },
        warning: {
          main: "#FFB900", //amber-400
          light: "#FFD230", //amber-300
          dark: "#FE9900", //amber-500
        },
      },
    },
    muiLocales.core,
    muiLocales.dataGrid
  );

  return (
    <div>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </div>
  );
};

export default BrandingWrapper;
